import { useEffect, useReducer } from "react";
import { Button, Input, Modal, Select } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  InfoCircleFilled,
  UndoOutlined,
} from "@ant-design/icons";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import RoleSelect from "@/components/Admin/RoleSelect";
import {
  adminAccountTypeOptions,
  type AdminStudyPermission,
  type AdminStudyPermissionAccess,
  type AdminUser,
} from "./adminUsersAccessData";
import {
  formatRoleLabel,
  getRoleDotColor,
} from "./adminRolesPermissionsMappers";
import { REVOKE_ALL_STUDY_ACCESS_DESCRIPTION } from "./adminUsersAccessMessaging";

const INFO_BANNER_CONTAINER_CLASS =
  "flex items-start gap-2.5 rounded-lg border border-[#0000000a] bg-[#ECFAFF99] px-4 py-3 text-[#20353E]";
const INFO_BANNER_ICON_CLASS = "mt-0.5 text-[#06709B]";

export type EditUserSettingsSubmitPayload = {
  clientAdmin: boolean;
  retainedPermissions: { trialId: string; role: string }[];
  revokedTrialIds: string[];
  firstName: string;
  lastName: string;
};

type EditablePermission = AdminStudyPermission & {
  revoked: boolean;
};

type EditUserSettingsFormState = {
  firstName: string;
  lastName: string;
  accountType: AdminUser["accountType"];
  permissions: EditablePermission[];
};

type EditUserSettingsFormAction =
  | {
      type: "reset";
      user: AdminUser;
    }
  | {
      type: "set-first-name";
      value: string;
    }
  | {
      type: "set-last-name";
      value: string;
    }
  | {
      type: "set-account-type";
      value: AdminUser["accountType"];
    }
  | {
      type: "set-permission-access";
      permissionId: string;
      access: AdminStudyPermissionAccess;
    }
  | {
      type: "revoke-permission";
      permissionId: string;
    }
  | {
      type: "restore-permission";
      permissionId: string;
    };

const EMPTY_FORM_STATE: EditUserSettingsFormState = {
  firstName: "",
  lastName: "",
  accountType: adminAccountTypeOptions[0],
  permissions: [],
};

function buildFormState(user: AdminUser): EditUserSettingsFormState {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    accountType: user.accountType,
    permissions: user.studyPermissions.map((permission) => ({
      ...permission,
      revoked: false,
    })),
  };
}

function formReducer(
  state: EditUserSettingsFormState,
  action: EditUserSettingsFormAction,
): EditUserSettingsFormState {
  switch (action.type) {
    case "reset":
      return buildFormState(action.user);
    case "set-first-name":
      return { ...state, firstName: action.value };
    case "set-last-name":
      return { ...state, lastName: action.value };
    case "set-account-type":
      return { ...state, accountType: action.value };
    case "set-permission-access":
      return {
        ...state,
        permissions: state.permissions.map((permission) =>
          permission.id === action.permissionId
            ? { ...permission, access: action.access }
            : permission,
        ),
      };
    case "revoke-permission":
      return {
        ...state,
        permissions: state.permissions.map((permission) =>
          permission.id === action.permissionId
            ? { ...permission, revoked: true }
            : permission,
        ),
      };
    case "restore-permission":
      return {
        ...state,
        permissions: state.permissions.map((permission) =>
          permission.id === action.permissionId
            ? { ...permission, revoked: false }
            : permission,
        ),
      };
    default:
      return state;
  }
}

type EditUserSettingsModalProps = {
  user: AdminUser | null;
  open: boolean;
  onClose: () => void;
  onSubmit?: (payload: EditUserSettingsSubmitPayload) => Promise<void> | void;
  isSubmitting?: boolean;
  submitError?: string | null;
  isLastAdmin?: boolean;
  roleOptionsByTrialId?: Record<string, string[]>;
};

function FieldLabel({ children }: { children: string }) {
  return (
    <span className="text-base font-medium leading-none tracking-[-0.16px] text-[#2B4752]">
      {children}
    </span>
  );
}

function accessOption(access: AdminStudyPermissionAccess) {
  return {
    value: access,
    title: formatRoleLabel(access),
    label: (
      <span className="flex items-center gap-2">
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: getRoleDotColor(access) }}
        />
        <span className="text-sm font-normal leading-none tracking-[-0.14px] text-black">
          {formatRoleLabel(access)}
        </span>
      </span>
    ),
  };
}

const adminModalShellProps = {
  style: { top: 0, padding: 0, paddingBottom: 0 },
  wrapClassName: "no-padding-modal",
};

function buildPermissionRoleOptions(
  permission: AdminStudyPermission,
  roleOptionsByTrialId: Record<string, string[]>,
) {
  const trialRoles = roleOptionsByTrialId[permission.trialId] ?? [];

  if (permission.access && !trialRoles.includes(permission.access)) {
    return [permission.access, ...trialRoles];
  }

  return trialRoles;
}

export default function EditUserSettingsModal({
  user,
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  submitError = null,
  isLastAdmin = false,
  roleOptionsByTrialId = {},
}: EditUserSettingsModalProps) {
  const [formState, dispatch] = useReducer(formReducer, EMPTY_FORM_STATE);

  useEffect(() => {
    if (!user) {
      return;
    }

    dispatch({ type: "reset", user });
  }, [user]);

  if (!user) {
    return null;
  }

  const handlePermissionAccessChange = (
    permissionId: string,
    access: AdminStudyPermissionAccess,
  ) => {
    dispatch({ type: "set-permission-access", permissionId, access });
  };

  const handleRemovePermission = (permissionId: string) => {
    dispatch({ type: "revoke-permission", permissionId });
  };

  const handleRestorePermission = (permissionId: string) => {
    dispatch({ type: "restore-permission", permissionId });
  };

  const handleUpdate = () => {
    if (!onSubmit) {
      onClose();
      return;
    }

    const retainedPermissions = formState.permissions
      .filter((permission) => !permission.revoked)
      .map((permission) => ({
        trialId: permission.trialId,
        role: permission.access,
      }));
    const revokedTrialIds = formState.permissions
      .filter((permission) => permission.revoked)
      .map((permission) => permission.trialId);

    void onSubmit({
      clientAdmin: formState.accountType === "ADMIN",
      retainedPermissions,
      revokedTrialIds,
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
    });
  };

  const hasNoActiveStudyAccess = formState.permissions.every(
    (permission) => permission.revoked,
  );
  const showNoStudyAccessBanner =
    formState.accountType !== "ADMIN" && hasNoActiveStudyAccess;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      width={840}
      {...adminModalShellProps}
      footer={null}
      closeIcon={null}
      destroyOnHidden
      styles={
        {
          content: {
            padding: 0,
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: "0px 4px 20px 0px rgba(0,0,0,0.10)",
          },
          body: { padding: 0 },
        } as any
      }
    >
      <div
        data-testid="edit-user-settings-modal"
        className="flex h-[710px] flex-col justify-between bg-white"
      >
        <div>
          <div className="flex h-12 items-start border-b border-[#0000000f] pt-3">
            <div className="flex h-6 w-full items-center justify-between px-3">
              <h2 className="m-0 text-lg font-medium leading-none text-[#000000d9]">
                Edit user settings
              </h2>
              <button
                type="button"
                aria-label="Close edit user settings"
                className="flex h-5 w-5 items-center justify-center rounded text-[#394C54] hover:bg-[#EFF1F2]"
                onClick={onClose}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          <div className="flex h-[606px] bg-white">
            <section className="flex h-full w-[301px] shrink-0 flex-col gap-3 overflow-hidden border-r border-[#0000000f] bg-white px-4 pb-2 pt-4">
              <div className="flex flex-1 flex-col gap-4">
                <AvatarWithInitials
                  name={
                    `${formState.firstName} ${formState.lastName}`.trim() ||
                    user.email
                  }
                  avatarSize={32}
                  avatarFontSize={16}
                  showLabel={false}
                />
                <p>{user.email}</p>

                <label className="flex flex-col gap-2">
                  <FieldLabel>First name</FieldLabel>
                  <Input
                    value={formState.firstName}
                    onChange={(event) =>
                      dispatch({
                        type: "set-first-name",
                        value: event.target.value,
                      })
                    }
                    className="h-[35px] rounded-lg border-[#0000001f] px-2 py-1 text-sm font-normal tracking-[-0.14px] text-[#000000d9]"
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <FieldLabel>Last name</FieldLabel>
                  <Input
                    value={formState.lastName}
                    onChange={(event) =>
                      dispatch({
                        type: "set-last-name",
                        value: event.target.value,
                      })
                    }
                    className="h-[35px] rounded-lg border-[#0000001f] px-2 py-1 text-sm font-normal tracking-[-0.14px] text-[#000000d9]"
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <FieldLabel>Account type</FieldLabel>
                  <Select
                    value={formState.accountType}
                    onChange={(value) =>
                      dispatch({ type: "set-account-type", value })
                    }
                    options={adminAccountTypeOptions.map((option) => ({
                      value: option,
                      label: option === "ADMIN" ? "Admin" : "User",
                      disabled: isLastAdmin && option === "USER",
                    }))}
                    suffixIcon={<span className="text-[#394C54]">›</span>}
                    className="h-[35px] w-full [&_.ant-select-selector]:rounded-lg! [&_.ant-select-selector]:border-[#0000001f]! [&_.ant-select-selector]:px-2! [&_.ant-select-selector]:py-[5px]! [&_.ant-select-selection-item]:text-sm! [&_.ant-select-selection-item]:font-normal! [&_.ant-select-selection-item]:tracking-[-0.14px]! [&_.ant-select-selection-item]:text-[#070B0D]!"
                  />
                </label>
              </div>
            </section>

            <section className="flex h-full flex-1 flex-col overflow-hidden bg-white px-4 pb-2 pt-4">
              <h3 className="m-0 text-base font-medium leading-none tracking-[-0.16px] text-[#2B4752]">
                Study Permissions
              </h3>

              <div className="mt-2 flex flex-col gap-3">
                {showNoStudyAccessBanner ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className={INFO_BANNER_CONTAINER_CLASS}
                  >
                    <InfoCircleFilled className={INFO_BANNER_ICON_CLASS} />
                    <p className="m-0 text-sm leading-[16px] tracking-[-0.14px]">
                      {REVOKE_ALL_STUDY_ACCESS_DESCRIPTION}
                    </p>
                  </div>
                ) : null}

                <div className="flex flex-col rounded-lg bg-[#00000005] py-3">
                  {formState.permissions.map((permission) => {
                    const isRevoked = permission.revoked;

                    return (
                      <div
                        key={permission.id}
                        className={`flex min-h-[35px] w-full items-center gap-4 px-4 py-1.5 ${
                          isRevoked ? "opacity-55" : ""
                        }`}
                      >
                        <div
                          className={`flex min-w-0 flex-1 items-center overflow-hidden rounded-lg py-[5px] ${
                            isRevoked ? "line-through" : ""
                          }`}
                        >
                          <span className="truncate rounded bg-[#0000000a] px-1 py-1 text-sm leading-none tracking-[-0.14px] text-[#070B0D]">
                            <span className="font-medium">
                              {permission.studyLabel.split(" | ")[0]}
                            </span>
                            <span className="font-normal">
                              {permission.studyLabel.includes(" | ")
                                ? ` | ${permission.studyLabel.split(" | ")[1]}`
                                : ""}
                            </span>
                          </span>
                        </div>
                        {isRevoked ? (
                          <>
                            <div className="flex h-[35px] w-[200px] shrink-0 items-center px-2 py-[5px]">
                              <span className="text-sm font-medium tracking-[-0.14px] text-[#980000] uppercase">
                                REVOKED
                              </span>
                            </div>
                            <button
                              type="button"
                              aria-label={`Restore ${permission.studyLabel} access`}
                              className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-black hover:bg-white"
                              onClick={() =>
                                handleRestorePermission(permission.id)
                              }
                            >
                              <UndoOutlined style={{ fontSize: 16 }} />
                            </button>
                          </>
                        ) : (
                          <>
                            <RoleSelect
                              value={permission.access}
                              onChange={(value) =>
                                handlePermissionAccessChange(
                                  permission.id,
                                  value,
                                )
                              }
                              options={buildPermissionRoleOptions(
                                permission,
                                roleOptionsByTrialId,
                              ).map(accessOption)}
                              suffixIcon={
                                <span className="text-[#394C54]">›</span>
                              }
                              className="h-[35px] w-[200px] shrink-0 [&_.ant-select-selector]:rounded-lg! [&_.ant-select-selector]:border-[#0000001f]! [&_.ant-select-selector]:px-2! [&_.ant-select-selector]:py-[5px]!"
                            />
                            <button
                              type="button"
                              aria-label={`Remove ${permission.studyLabel} access`}
                              className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#980000] hover:bg-white"
                              onClick={() =>
                                handleRemovePermission(permission.id)
                              }
                            >
                              <DeleteOutlined style={{ fontSize: 16 }} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex h-14 shrink-0 items-center justify-between border-t border-[#0000000f] p-3">
          <Button
            type="default"
            className="h-8 rounded border-[#00000073] px-3 font-medium text-[#2B4752]"
            disabled={isSubmitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          {submitError ? (
            <span
              role="alert"
              className="mx-3 min-w-0 flex-1 truncate text-right text-sm text-[#980000]"
            >
              {submitError}
            </span>
          ) : null}
          <Button
            type="primary"
            className="h-8 rounded bg-[#0C98D0]! px-3 font-medium text-white shadow-none hover:bg-[#06709B]!"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleUpdate}
          >
            Update
          </Button>
        </div>
      </div>
    </Modal>
  );
}
